<template>
  <div class="ai-corr">
    <section class="ai-page-head">
      <div class="ai-page-head__crumb">
        <router-link to="/data-visualization">시각화</router-link>
        <span>›</span>
        <em>데이터 연관관계</em>
      </div>
      <h2 class="ai-page-head__title">데이터 연관관계</h2>
      <p class="ai-page-head__sub">
        반도체 소재 계열(공정도)을 선택하고, 공정 항목(상위)과 속성(하위)을 골라 피어슨 상관계수를 히트맵으로 확인합니다.
        셀을 클릭하면 두 속성의 산점도를 볼 수 있고, 산점도에서는 선형·로그·2차·지수·거듭제곱 피팅을 R² 로 비교합니다.
        아래 <b>상관 순위</b>는 선택과 무관하게 전체 속성 쌍 중 상관이 높은 순서로 추천합니다.
      </p>
      <div class="ai-data-banner">
        <b>허브 실험입력 데이터</b>(작업본 최신) 기반 분석입니다. 값이 없는 항목은 표시되지 않습니다.
      </div>
    </section>

    <!-- 계열 선택 -->
    <section class="ai-series">
      <span class="ai-series__label">계열 선택</span>
      <button
        v-for="s in seriesList"
        :key="s.key"
        type="button"
        class="ai-series__btn"
        :class="{ 'is-on': s.key === seriesKey, 'is-off': s.disabled }"
        :disabled="s.disabled"
        :title="s.disabled ? '데이터 없음' : s.processName"
        @click="selectSeries(s.key)"
      >{{ s.name }}<small v-if="s.key === seriesKey && data"> · {{ data.rowCount }}건</small></button>
      <span v-if="loading" class="ai-series__loading">데이터 로딩 중…</span>
      <span v-else-if="loadError" class="ai-series__error">{{ loadError }}</span>
    </section>

    <!-- 상위항목 → 하위항목 선택 -->
    <section v-if="data" class="ai-corr__vars">
      <div class="ai-corr__vars-head">
        <span class="ai-corr__vars-label">분석 속성 선택</span>
        <small>숫자형이고 유효값 {{ MIN_VALID }}건 이상인 속성만 선택할 수 있습니다 · 선택 {{ selected.length }}/{{ MAX_SELECT }}</small>
        <button type="button" class="ai-corr__vars-reset" @click="autoSelect">추천 선택</button>
        <button type="button" class="ai-corr__vars-reset" @click="selected = []">모두 해제</button>
      </div>
      <div v-for="g in selectableGroups" :key="g.id" class="ai-group">
        <button type="button" class="ai-group__head" @click="toggleGroup(g.id)">
          <i :class="{ open: openGroups[g.id] }"></i>{{ g.name }}
          <small>{{ g.activeProps.length }}개 속성</small>
        </button>
        <div v-show="openGroups[g.id]" class="ai-group__body">
          <label
            v-for="p in g.activeProps"
            :key="p.id"
            class="ai-chip"
            :class="{ 'is-on': selected.includes(p.id) }"
            :title="`유효 ${p.validCount}건` + (p.unit ? ` · ${p.unit}` : '')"
          >
            <input type="checkbox" :value="p.id" v-model="selected" :disabled="!selected.includes(p.id) && selected.length >= MAX_SELECT" />
            {{ p.name }}<small v-if="p.unit"> ({{ p.unit }})</small>
          </label>
        </div>
      </div>
    </section>

    <section v-if="data" class="ai-corr__panels">
      <!-- 히트맵 -->
      <div class="ai-panel">
        <h3 class="ai-panel__title">상관계수 히트맵 <small>(피어슨 r · 쌍별 공통 표본 기준)</small></h3>
        <div class="ai-corr__heatwrap" v-if="activeVars.length >= 2">
          <svg :viewBox="`0 0 ${heatSize.w} ${heatSize.h}`" class="ai-corr__heatmap">
            <text
              v-for="(v, j) in activeVars"
              :key="'c' + v.id"
              :x="heatPad.left + j * cell + cell / 2"
              :y="heatPad.top - 10"
              :transform="`rotate(-38 ${heatPad.left + j * cell + cell / 2} ${heatPad.top - 10})`"
              class="ai-corr__lbl"
              text-anchor="start"
            >{{ shortName(v) }}</text>
            <text
              v-for="(v, i) in activeVars"
              :key="'r' + v.id"
              :x="heatPad.left - 8"
              :y="heatPad.top + i * cell + cell / 2 + 4"
              class="ai-corr__lbl"
              text-anchor="end"
            >{{ shortName(v) }}</text>
            <g v-for="(rv, i) in activeVars" :key="'row' + rv.id">
              <g
                v-for="(cv, j) in activeVars"
                :key="rv.id + '_' + cv.id"
                class="ai-corr__cell"
                :class="{ 'is-sel': pair && pair[0] === rv.id && pair[1] === cv.id }"
                @click="selectPair(rv.id, cv.id)"
                @mousemove="hoverCell($event, rv, cv)"
                @mouseleave="tooltip.show = false"
              >
                <rect
                  :x="heatPad.left + j * cell + 1"
                  :y="heatPad.top + i * cell + 1"
                  :width="cell - 2"
                  :height="cell - 2"
                  rx="3"
                  :fill="cellColor(rv, cv)"
                />
                <text
                  :x="heatPad.left + j * cell + cell / 2"
                  :y="heatPad.top + i * cell + cell / 2 + 4"
                  text-anchor="middle"
                  class="ai-corr__val"
                  :fill="cellTextColor(rv, cv)"
                >{{ cellText(rv, cv) }}</text>
              </g>
            </g>
          </svg>
          <div class="ai-corr__legend">
            <span>-1</span>
            <span class="ai-corr__legend-bar"></span>
            <span>+1</span>
            <span class="ai-corr__legend-note">· 표본 부족(n&lt;{{ MIN_VALID }}) 쌍은 "–"</span>
          </div>
          <div v-if="tooltip.show" class="ai-corr__tip" :style="{ left: tooltip.x + 'px', top: tooltip.y + 'px' }">
            {{ tooltip.text }}
          </div>
        </div>
        <p v-else class="ai-panel__empty">속성을 2개 이상 선택하세요.</p>
      </div>

      <!-- 산점도 -->
      <div class="ai-panel" ref="scatterPanel">
        <h3 class="ai-panel__title">산점도</h3>
        <template v-if="pairStat && pairStat.r !== null">
          <p class="ai-corr__pairinfo">
            <b>{{ propName(pair[1]) }}</b> (X) vs <b>{{ propName(pair[0]) }}</b> (Y) —
            r = <b :style="{ color: corrColor(pairStat.r, true) }">{{ pairStat.r.toFixed(3) }}</b>
            <span class="ai-corr__strength">({{ strengthLabel(pairStat.r) }} · n={{ pairStat.n }}<template v-if="pairDiv"> · 변동 표본 {{ pairDiv.varMin }} · 고유값 {{ pairDiv.distinctX }}/{{ pairDiv.distinctY }}</template>)</span>
          </p>
          <div class="ai-fit">
            <span class="ai-fit__label">피팅 모델</span>
            <button
              v-for="f in fits"
              :key="f.key"
              type="button"
              class="ai-fit__btn"
              :class="{ 'is-on': activeFit && f.key === activeFit.key, 'is-off': !f.ok }"
              :disabled="!f.ok"
              :title="fitTitle(f)"
              @click="fitKey = f.key"
            >{{ f.name }}<small v-if="f.ok"> R²={{ f.r2.toFixed(2) }}</small><i v-if="bestKey === f.key" class="ai-fit__best">최적</i></button>
          </div>
          <p v-if="activeFit" class="ai-fit__eq">
            <code>{{ fitEquation(activeFit) }}</code>
            <span>R² = {{ activeFit.r2.toFixed(3) }} · n = {{ activeFit.n }}<template v-if="activeFit.n < pairStat.n"> (조건 {{ activeFit.cond }} 에 맞는 점만)</template></span>
          </p>
          <svg :viewBox="`0 0 ${scat.w} ${scat.h}`" class="ai-corr__scatter">
            <line :x1="scat.pad" :y1="scat.h - scat.pad" :x2="scat.w - 16" :y2="scat.h - scat.pad" stroke="#ccd5dd" />
            <line :x1="scat.pad" :y1="16" :x2="scat.pad" :y2="scat.h - scat.pad" stroke="#ccd5dd" />
            <g v-for="t in 5" :key="'tx' + t">
              <text
                :x="scat.pad + ((t - 1) / 4) * (scat.w - 16 - scat.pad)"
                :y="scat.h - scat.pad + 18"
                text-anchor="middle"
                class="ai-corr__tick"
              >{{ tickVal(scatDomain.x, (t - 1) / 4) }}</text>
            </g>
            <g v-for="t in 5" :key="'ty' + t">
              <text
                :x="scat.pad - 8"
                :y="scat.h - scat.pad - ((t - 1) / 4) * (scat.h - 16 - scat.pad) + 4"
                text-anchor="end"
                class="ai-corr__tick"
              >{{ tickVal(scatDomain.y, (t - 1) / 4) }}</text>
            </g>
            <path
              v-if="curvePath"
              :d="curvePath"
              fill="none" stroke="#e0533d" stroke-width="2" stroke-dasharray="6 4" opacity="0.85"
            />
            <circle
              v-for="(pt, idx) in scatterPoints"
              :key="idx"
              :cx="scatX(pt.x)"
              :cy="scatY(pt.y)"
              r="4.5"
              fill="#0dbde9"
              fill-opacity="0.55"
              stroke="#0a93b5"
              stroke-width="1"
            />
            <text :x="(scat.pad + scat.w - 16) / 2" :y="scat.h - 6" text-anchor="middle" class="ai-corr__axis">
              {{ propName(pair[1]) }}
            </text>
            <text
              :x="14" :y="(scat.h - scat.pad + 16) / 2"
              text-anchor="middle" class="ai-corr__axis"
              :transform="`rotate(-90 14 ${(scat.h - scat.pad + 16) / 2})`"
            >{{ propName(pair[0]) }}</text>
          </svg>
        </template>
        <p v-else class="ai-panel__empty">히트맵에서 서로 다른 두 속성의 셀을 클릭하세요.<br/>(표본이 부족한 쌍은 산점도를 제공하지 않습니다)</p>
      </div>
    </section>

    <!-- 상관 순위(추천) -->
    <section v-if="data" class="ai-panel ai-rank">
      <h3 class="ai-panel__title">
        상관 순위 <small>(분석 가능 속성 {{ data.numericProps.length }}개의 전체 쌍 중 |r| 상위 · 선택 여부와 무관 · 행을 클릭하면 산점도로 이동 · "변동 표본"은 공통 표본 중 최빈값이 아닌 값을 가진 표본 수)</small>
      </h3>
      <div class="ai-rank__ctrl">
        <label>공통 표본 n ≥
          <select v-model.number="rankMinN">
            <option :value="MIN_VALID">{{ MIN_VALID }}</option>
            <option :value="10">10</option>
            <option :value="30">30</option>
            <option :value="100">100</option>
          </select>
        </label>
        <label :title="'공통 표본 중 최빈값이 아닌 값을 가진 표본 수(X·Y 중 작은 쪽). 값이 거의 한 가지인 열은 n 이 커도 여기서 걸러진다'">변동 표본 ≥
          <select v-model.number="rankMinVar">
            <option :value="0">0</option>
            <option :value="5">5</option>
            <option :value="10">10</option>
            <option :value="30">30</option>
            <option :value="100">100</option>
          </select>
        </label>
        <label><input type="checkbox" v-model="rankHidePerfect" /> 완전 상관(|r| ≥ 0.999) 숨김</label>
        <label><input type="checkbox" v-model="rankHideSameName" /> 같은 이름 속성 쌍 숨김</label>
        <label>표시
          <select v-model.number="rankLimit">
            <option :value="10">10</option>
            <option :value="20">20</option>
            <option :value="50">50</option>
          </select>개
        </label>
      </div>
      <div v-if="rankRows.length" class="ai-rank__wrap">
        <table class="ai-rank__table">
          <thead>
            <tr>
              <th>#</th><th>속성 X</th><th>속성 Y</th><th>r</th><th>강도</th><th>n</th>
              <th title="공통 표본 중 최빈값이 아닌 값을 가진 표본 수 (X·Y 중 작은 쪽)">변동 표본</th>
              <th title="공통 표본 안의 서로 다른 값 수 (X / Y)">고유값</th>
              <th>최적 피팅 (R²)</th><th></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, i) in rankRows"
              :key="row.a.id + '_' + row.b.id"
              :class="{ 'is-sel': isRankSel(row) }"
              @click="pickRank(row)"
            >
              <td class="ai-rank__num">{{ i + 1 }}</td>
              <td>{{ row.a.name }}<small v-if="row.a.unit"> ({{ row.a.unit }})</small><em>{{ row.a.groupName }}</em></td>
              <td>{{ row.b.name }}<small v-if="row.b.unit"> ({{ row.b.unit }})</small><em>{{ row.b.groupName }}</em></td>
              <td class="ai-rank__r"><b :style="{ color: corrColor(row.r, true) }">{{ row.r.toFixed(3) }}</b></td>
              <td>{{ strengthLabel(row.r) }}</td>
              <td class="ai-rank__num">{{ row.n }}</td>
              <td class="ai-rank__num">{{ row.dv.varMin }}</td>
              <td class="ai-rank__num">{{ row.dv.distinctX }} / {{ row.dv.distinctY }}</td>
              <td>
                <template v-if="row.best">{{ row.best.name }} <small>R²={{ row.best.r2.toFixed(3) }}</small></template>
                <span v-else>–</span>
              </td>
              <td class="ai-rank__go">산점도 →</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-else class="ai-panel__empty">조건에 맞는 속성 쌍이 없습니다. 공통 표본 하한을 낮춰 보세요.</p>
    </section>
  </div>
</template>

<script>
import { SERIES, MIN_VALID, resolveSeries, loadSeries, pearson, pairPoints } from "./lib/dvData";
import { fitAll, bestFit, fitEquation, rankPairs, diversity } from "./lib/fit";

export default {
  name: "DvCorrelation",
  data() {
    return {
      MIN_VALID,
      MAX_SELECT: 12,
      seriesKey: null,
      seriesProc: null, // resolveSeries 결과
      data: null,
      loading: false,
      loadError: null,
      selected: [], // processPropertyId[]
      openGroups: {},
      pair: null, // [yPropId, xPropId]
      cell: 64,
      heatPad: { left: 170, top: 120 },
      scat: { w: 520, h: 400, pad: 56 },
      tooltip: { show: false, x: 0, y: 0, text: "" },
      corrCache: {},
      fitKey: "linear", // 산점도 피팅 모델 (fit.js FIT_MODELS key)
      rankMinN: 10,
      rankMinVar: 10, // 변동 표본 하한 (fit.js diversity.varMin)
      rankHidePerfect: true,
      rankHideSameName: true, // 예: n형 층·p형 층에 같은 이름(Channel width)으로 있는 속성끼리의 쌍
      rankLimit: 20,
    };
  },
  computed: {
    seriesList() {
      return SERIES.map((s) => ({
        ...s,
        disabled: this.seriesProc ? !this.seriesProc[s.key] : false,
        processName: this.seriesProc && this.seriesProc[s.key] ? this.seriesProc[s.key].processName : "",
      }));
    },
    selectableGroups() {
      if (!this.data) return [];
      return this.data.groups
        .map((g) => ({ ...g, activeProps: g.props.filter((p) => p.active) }))
        .filter((g) => g.activeProps.length > 0);
    },
    propMap() {
      const m = {};
      if (this.data) this.data.groups.forEach((g) => g.props.forEach((p) => { m[p.id] = p; }));
      return m;
    },
    activeVars() {
      return this.selected.map((id) => this.propMap[id]).filter(Boolean);
    },
    heatSize() {
      const n = this.activeVars.length;
      return {
        w: this.heatPad.left + n * this.cell + 20,
        h: this.heatPad.top + n * this.cell + 16,
      };
    },
    pairStat() {
      if (!this.pair || this.pair[0] === this.pair[1]) return null;
      const py = this.propMap[this.pair[0]];
      const px = this.propMap[this.pair[1]];
      if (!px || !py) return null;
      return this.corr(py, px);
    },
    scatterPoints() {
      if (!this.pairStat) return [];
      return pairPoints(this.propMap[this.pair[1]], this.propMap[this.pair[0]]);
    },
    scatDomain() {
      const pts = this.scatterPoints;
      const pad = (lo, hi) => {
        if (lo === hi) { lo -= 1; hi += 1; }
        const m = (hi - lo) * 0.06;
        return { min: lo - m, max: hi + m };
      };
      if (!pts.length) return { x: { min: 0, max: 1 }, y: { min: 0, max: 1 } };
      return {
        x: pad(Math.min(...pts.map((p) => p.x)), Math.max(...pts.map((p) => p.x))),
        y: pad(Math.min(...pts.map((p) => p.y)), Math.max(...pts.map((p) => p.y))),
      };
    },
    // ─── 피팅 ───
    fits() {
      return this.scatterPoints.length ? fitAll(this.scatterPoints) : [];
    },
    bestKey() {
      const ok = this.fits.filter((f) => f.ok);
      return ok.length ? ok.reduce((a, b) => (b.r2 > a.r2 ? b : a)).key : null;
    },
    activeFit() {
      const f = this.fits.find((x) => x.key === this.fitKey && x.ok);
      if (f) return f;
      return this.fits.find((x) => x.key === this.bestKey) || null;
    },
    curvePath() {
      const f = this.activeFit;
      if (!f || !f.ok) return null;
      const d = this.scatDomain;
      const N = 80;
      let path = "", pen = false;
      for (let i = 0; i <= N; i++) {
        const x = d.x.min + ((d.x.max - d.x.min) * i) / N;
        const y = f.predict(x);
        // 정의역 밖(로그·거듭제곱의 x≤0)이나 그림 범위를 크게 벗어나면 선을 끊는다
        if (!isFinite(y) || y < d.y.min - (d.y.max - d.y.min) || y > d.y.max + (d.y.max - d.y.min)) { pen = false; continue; }
        path += (pen ? " L" : " M") + this.scatX(x).toFixed(1) + " " + this.scatY(y).toFixed(1);
        pen = true;
      }
      return path || null;
    },
    pairDiv() {
      if (!this.pair || !this.pairStat) return null;
      const px = this.propMap[this.pair[1]], py = this.propMap[this.pair[0]];
      return px && py ? diversity(px, py) : null;
    },
    // ─── 상관 순위 ───
    rankRows() {
      if (!this.data) return [];
      const rows = rankPairs(this.data.numericProps, {
        minN: this.rankMinN, minVar: this.rankMinVar, hidePerfect: this.rankHidePerfect, hideSameName: this.rankHideSameName, limit: this.rankLimit,
      });
      return rows.map((r) => ({ ...r, best: bestFit(pairPoints(r.a, r.b)) }));
    },
  },
  async mounted() {
    const q = String(this.$route.query.series || "").toUpperCase();
    this.seriesKey = SERIES.some((s) => s.key === q) ? q : SERIES[0].key;
    try {
      this.seriesProc = await resolveSeries(this.$wApi);
    } catch (e) { /* 공정도 목록 실패 시에도 로딩 시도는 가능 */ }
    await this.load();
  },
  methods: {
    async selectSeries(key) {
      if (key === this.seriesKey) return;
      this.seriesKey = key;
      if (String(this.$route.query.series || "").toUpperCase() !== key) {
        this.$router.replace({ query: { ...this.$route.query, series: key } });
      }
      await this.load();
    },
    async load() {
      this.loading = true;
      this.loadError = null;
      this.data = null;
      this.pair = null;
      this.corrCache = {};
      try {
        const d = await loadSeries(this.$wApi, this.seriesKey);
        if (!d) { this.loadError = "해당 계열의 공정도를 찾지 못했습니다."; return; }
        this.data = d;
        const open = {};
        d.groups.forEach((g, i) => { open[g.id] = i < 3; });
        this.openGroups = open;
        this.autoSelect();
      } catch (e) {
        this.loadError = "데이터 로딩 실패: " + ((e && e.message) || e);
      } finally {
        this.loading = false;
      }
    },
    autoSelect() {
      if (!this.data) return;
      // 추천: 유효값이 많은 숫자 속성 상위 8개
      const top = [...this.data.numericProps]
        .sort((a, b) => b.validCount - a.validCount)
        .slice(0, 8);
      this.selected = top.map((p) => p.id);
      this.pair = top.length >= 2 ? [top[0].id, top[1].id] : null;
    },
    toggleGroup(id) {
      this.$set(this.openGroups, id, !this.openGroups[id]);
    },
    propName(id) {
      const p = this.propMap[id];
      return p ? p.name + (p.unit ? ` (${p.unit})` : "") : id;
    },
    shortName(p) {
      const s = p.name + (p.unit ? ` (${p.unit})` : "");
      return s.length > 22 ? s.slice(0, 21) + "…" : s;
    },
    corr(pa, pb) {
      if (pa.id === pb.id) return { r: 1, n: pa.validCount };
      const key = pa.id < pb.id ? pa.id + "|" + pb.id : pb.id + "|" + pa.id;
      if (!this.corrCache[key]) this.corrCache[key] = pearson(pa, pb);
      return this.corrCache[key];
    },
    cellStat(rv, cv) { return this.corr(rv, cv); },
    cellText(rv, cv) {
      const s = this.cellStat(rv, cv);
      return s.r === null ? "–" : s.r.toFixed(2);
    },
    cellColor(rv, cv) {
      const s = this.cellStat(rv, cv);
      return s.r === null ? "#f3f5f7" : this.corrColor(s.r);
    },
    cellTextColor(rv, cv) {
      const s = this.cellStat(rv, cv);
      if (s.r === null) return "#aab";
      return Math.abs(s.r) > 0.55 ? "#fff" : "#334";
    },
    corrColor(r, forText) {
      const t = Math.max(-1, Math.min(1, r));
      const mix = (c1, c2, k) => Math.round(c1 + (c2 - c1) * k);
      let rgb;
      if (t >= 0) {
        const k = forText ? Math.max(t, 0.45) : t;
        rgb = [mix(255, 199, k), mix(255, 57, k), mix(255, 36, k)]; // white → #c73924
      } else {
        const k = forText ? Math.max(-t, 0.45) : -t;
        rgb = [mix(255, 23, k), mix(255, 93, k), mix(255, 162, k)]; // white → #175da2
      }
      return `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`;
    },
    strengthLabel(r) {
      const a = Math.abs(r);
      const dir = r >= 0 ? "양의" : "음의";
      if (a >= 0.7) return `강한 ${dir} 상관`;
      if (a >= 0.4) return `뚜렷한 ${dir} 상관`;
      if (a >= 0.2) return `약한 ${dir} 상관`;
      return "상관 거의 없음";
    },
    selectPair(rowId, colId) {
      if (rowId === colId) return;
      this.pair = [rowId, colId];
    },
    fitEquation,
    fitTitle(f) {
      if (f.ok) return f.formula + " · R² = " + f.r2.toFixed(4) + " · n=" + f.n;
      return f.formula + " — 적용 불가" + (f.cond ? " (조건 " + f.cond + ")" : " (표본 부족)");
    },
    isRankSel(row) {
      return !!this.pair && ((this.pair[1] === row.a.id && this.pair[0] === row.b.id) || (this.pair[1] === row.b.id && this.pair[0] === row.a.id));
    },
    pickRank(row) {
      // X = a, Y = b. 히트맵에도 보이도록 선택 목록에 여유가 있으면 추가한다.
      for (const id of [row.a.id, row.b.id]) {
        if (!this.selected.includes(id) && this.selected.length < this.MAX_SELECT) this.selected.push(id);
      }
      this.pair = [row.b.id, row.a.id];
      this.$nextTick(() => {
        const el = this.$refs.scatterPanel;
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    },
    hoverCell(ev, rv, cv) {
      const wrap = ev.currentTarget.closest(".ai-corr__heatwrap");
      const rect = wrap.getBoundingClientRect();
      const s = this.cellStat(rv, cv);
      this.tooltip = {
        show: true,
        x: ev.clientX - rect.left + 14,
        y: ev.clientY - rect.top + 10,
        text: s.r === null
          ? `${rv.name} × ${cv.name} : 공통 표본 부족 (n=${s.n})`
          : `${rv.name} × ${cv.name} : r = ${s.r.toFixed(3)} (n=${s.n})`,
      };
    },
    scatX(v) {
      const d = this.scatDomain.x;
      const k = (v - d.min) / (d.max - d.min);
      return this.scat.pad + Math.max(0, Math.min(1, k)) * (this.scat.w - 16 - this.scat.pad);
    },
    scatY(v) {
      const d = this.scatDomain.y;
      const k = (v - d.min) / (d.max - d.min);
      return this.scat.h - this.scat.pad - Math.max(0, Math.min(1, k)) * (this.scat.h - 16 - this.scat.pad);
    },
    tickVal(dom, k) {
      const v = dom.min + (dom.max - dom.min) * k;
      const a = Math.abs(v);
      if (a >= 1000) return Math.round(v).toLocaleString();
      if (a >= 100) return String(Math.round(v));
      if (a >= 1) return v.toFixed(1);
      return v.toFixed(3);
    },
  },
};
</script>

<style lang="scss" scoped>
.ai-corr {
  max-width: 1200px;
  margin: 0 auto;
  padding: 44px 20px 120px;
}

/* 공통 페이지 머리(지식그래프 페이지와 동일 톤) */
.ai-page-head {
  margin-bottom: 22px;
  &__crumb {
    font-size: 13px;
    color: #789;
    margin-bottom: 10px;
    a {
      color: #789;
      text-decoration: none;
      &:hover { color: #003f67; text-decoration: underline; }
    }
    span { margin: 0 6px; }
    em { font-style: normal; color: #003f67; font-weight: 600; }
  }
  &__title {
    font-size: 26px;
    font-weight: 700;
    color: #111;
    letter-spacing: -0.5px;
  }
  &__sub {
    margin-top: 10px;
    font-size: 14px;
    color: #556;
    line-height: 22px;
  }
}
.ai-data-banner {
  margin-top: 14px;
  padding: 10px 14px;
  border: 1px solid #c9e7f5;
  background: #f2fafe;
  border-radius: 8px;
  font-size: 13px;
  color: #0a5f80;
  b { font-weight: 700; }
}

/* 계열 선택 */
.ai-series {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 18px;
  &__label { font-size: 13px; font-weight: 700; color: #334; margin-right: 4px; }
  &__btn {
    height: 36px;
    padding: 0 18px;
    border: 1px solid #ccd5dd;
    border-radius: 18px;
    background: #fff;
    font-size: 14px;
    font-weight: 600;
    color: #556;
    cursor: pointer;
    small { font-weight: 400; font-size: 12px; }
    &.is-on { background: #003f67; border-color: #003f67; color: #fff; }
    &.is-off { opacity: 0.4; cursor: not-allowed; }
    &:not(.is-on):not(.is-off):hover { border-color: #0dbde9; }
  }
  &__loading { font-size: 13px; color: #0a93b5; }
  &__error { font-size: 13px; color: #c0392b; }
}

/* 상위항목(그룹) → 하위항목(속성) 선택 */
.ai-corr__vars {
  border: 1px solid #e2e8ee;
  border-radius: 12px;
  background: #fbfdfe;
  padding: 14px 16px;
  margin-bottom: 20px;
  max-height: 320px;
  overflow-y: auto;
}
.ai-corr__vars-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  flex-wrap: wrap;
  small { color: #789; font-size: 12px; }
}
.ai-corr__vars-label { font-size: 13px; font-weight: 700; color: #334; }
.ai-corr__vars-reset {
  height: 26px;
  padding: 0 10px;
  font-size: 12px;
  border: 1px solid #ccd5dd;
  border-radius: 6px;
  background: #fff;
  color: #556;
  cursor: pointer;
  &:hover { border-color: #003f67; color: #003f67; }
  &:first-of-type { margin-left: auto; }
}
.ai-group {
  border-top: 1px solid #eef2f5;
  &__head {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 2px;
    background: none;
    border: none;
    font-size: 13.5px;
    font-weight: 700;
    color: #003f67;
    cursor: pointer;
    text-align: left;
    i {
      width: 0; height: 0;
      border-left: 5px solid #003f67;
      border-top: 4px solid transparent;
      border-bottom: 4px solid transparent;
      transition: transform 0.12s;
      &.open { transform: rotate(90deg); }
    }
    small { font-weight: 400; color: #89a; }
  }
  &__body {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 2px 2px 10px 16px;
  }
}
.ai-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 11px;
  border: 1px solid #ccd5dd;
  border-radius: 16px;
  font-size: 12.5px;
  color: #556;
  cursor: pointer;
  user-select: none;
  background: #fff;
  small { font-size: 11px; color: inherit; opacity: 0.75; margin-left: 2px; }
  input { display: none; }
  &.is-on {
    border-color: #003f67;
    background: #003f67;
    color: #fff;
  }
}

.ai-corr__panels {
  display: flex;
  gap: 24px;
  align-items: flex-start;
  flex-wrap: wrap;
}
.ai-panel {
  flex: 1 1 480px;
  min-width: 420px;
  border: 1px solid #dde6ec;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 4px 16px rgba(9, 76, 177, 0.06);
  padding: 22px 24px;
  &__title {
    font-size: 16px;
    font-weight: 700;
    color: #003f67;
    margin-bottom: 16px;
    small { font-weight: 400; color: #789; font-size: 12px; }
  }
  &__empty {
    padding: 60px 0;
    text-align: center;
    color: #99a;
    font-size: 14px;
    line-height: 22px;
  }
}

.ai-corr__heatwrap { position: relative; overflow-x: auto; }
.ai-corr__heatmap { width: 100%; height: auto; min-width: 420px; }
.ai-corr__lbl { font-size: 11px; fill: #334; }
.ai-corr__val { font-size: 12px; pointer-events: none; }
.ai-corr__cell {
  cursor: pointer;
  rect { transition: opacity 0.1s; }
  &:hover rect { opacity: 0.75; stroke: #003f67; stroke-width: 1.5; }
  &.is-sel rect { stroke: #003f67; stroke-width: 2; }
}
.ai-corr__legend {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  font-size: 12px;
  color: #556;
  &-bar {
    width: 160px;
    height: 10px;
    border-radius: 5px;
    background: linear-gradient(to right, #175da2, #fff, #c73924);
    border: 1px solid #e2e8ee;
  }
  &-note { color: #9ab; }
}
.ai-corr__tip {
  position: absolute;
  padding: 5px 9px;
  background: rgba(17, 34, 51, 0.92);
  color: #fff;
  font-size: 12px;
  border-radius: 6px;
  pointer-events: none;
  white-space: nowrap;
  z-index: 5;
}

.ai-fit {
  display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin: 4px 0 6px;
  &__label { font-size: 12px; font-weight: 700; color: #334; margin-right: 2px; }
  &__btn {
    position: relative; padding: 4px 10px; border: 1px solid #dde6ec; border-radius: 999px; background: #fff;
    font-size: 12px; color: #334; cursor: pointer; line-height: 18px;
    small { color: #789; margin-left: 2px; }
    &.is-on { background: #003f67; border-color: #003f67; color: #fff; small { color: #c7d8e4; } }
    &.is-off { color: #aab; background: #f6f8fa; cursor: not-allowed; }
  }
  &__best {
    display: inline-block; margin-left: 5px; padding: 0 5px; border-radius: 8px; background: #e0533d; color: #fff;
    font-size: 10px; font-style: normal; line-height: 15px; vertical-align: 1px;
  }
  &__eq {
    margin: 0 0 6px; font-size: 12px; color: #334;
    code { font-family: Consolas, Menlo, monospace; font-size: 12px; background: #f3f6f9; padding: 2px 6px; border-radius: 4px; }
    span { margin-left: 8px; color: #789; }
  }
}
.ai-rank {
  margin-top: 20px;
  &__ctrl {
    display: flex; flex-wrap: wrap; gap: 16px; align-items: center; margin-bottom: 10px; font-size: 12px; color: #334;
    // 전역 select 는 appearance:none + 오른쪽 caret 배경이라, 화살표 자리를 비워 두지 않으면 숫자와 겹친다
    select {
      min-width: 64px; margin: 0 4px; padding: 3px 22px 3px 8px; border: 1px solid #dde6ec; border-radius: 4px;
      font-size: 12px; line-height: 16px; color: #223; background-color: #fff;
    }
    input[type="checkbox"] { vertical-align: -2px; margin-right: 4px; }
  }
  &__wrap { overflow-x: auto; }
  &__table {
    width: 100%; border-collapse: collapse; font-size: 12.5px;
    th { text-align: left; padding: 8px 8px; background: #f3f6f9; color: #334; font-weight: 700; border-bottom: 1px solid #dde6ec; white-space: nowrap; }
    td { padding: 7px 8px; border-bottom: 1px solid #eef2f5; color: #223; vertical-align: top; }
    td small { color: #789; }
    td em { display: block; font-style: normal; font-size: 11px; color: #789; margin-top: 1px; }
    tbody tr { cursor: pointer; }
    tbody tr:hover { background: #f6fbfe; }
    tbody tr.is-sel { background: #e8f6fb; }
  }
  &__num { text-align: right; color: #556; white-space: nowrap; }
  &__r { white-space: nowrap; }
  &__go { color: #0a93b5; font-weight: 600; white-space: nowrap; }
}
.ai-corr__pairinfo {
  font-size: 13px;
  color: #334;
  margin-bottom: 10px;
  b { font-weight: 700; }
}
.ai-corr__strength { color: #789; margin-left: 4px; }
.ai-corr__scatter { width: 100%; height: auto; }
.ai-corr__tick { font-size: 10px; fill: #789; }
.ai-corr__axis { font-size: 12px; fill: #334; font-weight: 600; }
</style>
